import React, { useEffect, useRef, useState } from 'react';
import './StarWars.css';

const StarWars = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [animationState, setAnimationState] = useState('stopped'); // 'stopped', 'prepared', 'jumping', 'decelerating'
  const [, setStars] = useState([]);
  const [showTextCrawl, setShowTextCrawl] = useState(false);
  const speedRef = useRef(0);
  const targetSpeedRef = useRef(0);
  const hasCompletedCycle = useRef(false);

  // Initialize stars with better distribution
  const initStars = () => {
    const starArray = [];
    for (let i = 0; i < 800; i++) {
      starArray.push({
        x: (Math.random() - 0.5) * 2000, // Wider distribution
        y: (Math.random() - 0.5) * 2000, // Wider distribution
        z: Math.random() * 1000,
        pz: 0, // Previous z for trail effect
        brightness: Math.random()
      });
    }
    setStars(starArray);
  };

  // Convert 3D coordinates to 2D screen coordinates
  const project3D = (x, y, z, width, height) => {
    const focalLength = 300;
    if (z <= 0) return null;
    
    return {
      x: (x / z) * focalLength + width / 2,
      y: (y / z) * focalLength + height / 2,
      size: (1 - z / 1000) * 2
    };
  };

  // Prepare for launch - gentle speed with streaks
  const prepareForLaunch = () => {
    if (animationState !== 'stopped') return;
    setAnimationState('prepared');
    targetSpeedRef.current = 0.5; // Gentle speed but with hyperspace effect
  };

  // Start hyperspace jump
  const startHyperspace = () => {
    if (animationState === 'jumping') return;
    setAnimationState('jumping');
    targetSpeedRef.current = 8; // Ludicrous speed!
    hasCompletedCycle.current = false;
    
    // After hyperspace effect, start deceleration
    setTimeout(() => {
      if (!hasCompletedCycle.current) {
        hasCompletedCycle.current = true;
        setAnimationState('decelerating');
        targetSpeedRef.current = 0; // Gradually stop
        
        // After deceleration, set to complete stop
        setTimeout(() => {
          setAnimationState('stopped');
          speedRef.current = 0;
          targetSpeedRef.current = 0;
        }, 4000); // 4 seconds to decelerate
      }
    }, 3000); // 3 seconds of hyperspace
  };

  // Start the iconic Star Wars text crawl
  const startTextCrawl = () => {
    setShowTextCrawl(true);
    
    // Hide text crawl after animation completes (60 seconds)
    setTimeout(() => {
      setShowTextCrawl(false);
    }, 60000);
  };

  // Animation loop
  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas with fade effect for trails
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, width, height);

    // Smoothly interpolate speed with different rates based on state
    const interpolationRate = animationState === 'decelerating' ? 0.02 : 0.05;
    speedRef.current += (targetSpeedRef.current - speedRef.current) * interpolationRate;

    setStars(prevStars => {
      return prevStars.map(star => {
        // Store previous z position for trail
        const newStar = { ...star };
        newStar.pz = star.z;
        
        // Move star toward camera only if not completely stopped
        if (animationState !== 'stopped' || speedRef.current > 0.01) {
          newStar.z -= speedRef.current * 100;
        }
        
        // Reset star if it's behind camera with wider distribution
        if (newStar.z <= 0) {
          newStar.x = (Math.random() - 0.5) * 2000; // Wider distribution to prevent clustering
          newStar.y = (Math.random() - 0.5) * 2000; // Wider distribution to prevent clustering
          newStar.z = 1000;
          newStar.pz = 1000;
        }

        // Project current and previous positions
        const currentPos = project3D(newStar.x, newStar.y, newStar.z, width, height);
        const prevPos = project3D(newStar.x, newStar.y, newStar.pz, width, height);

        if (currentPos && prevPos) {
          // Calculate star brightness based on speed and distance
          const speedBrightness = Math.min(speedRef.current * 50, 1);
          const distanceBrightness = (1 - newStar.z / 1000);
          const brightness = Math.max(newStar.brightness * distanceBrightness, speedBrightness);
          
          // Draw star trail (hyperspace streaks) or static stars
          if (speedRef.current > 0.05) {
            ctx.beginPath();
            ctx.moveTo(prevPos.x, prevPos.y);
            ctx.lineTo(currentPos.x, currentPos.y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${brightness})`;
            ctx.lineWidth = Math.max(currentPos.size, 0.5);
            ctx.stroke();
          } else {
            // Draw static star points when stopped
            const staticBrightness = animationState === 'stopped' ? newStar.brightness * 0.8 : brightness;
            ctx.beginPath();
            ctx.arc(currentPos.x, currentPos.y, Math.max(currentPos.size, 0.5), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${staticBrightness})`;
            ctx.fill();
          }
        }

        return newStar;
      });
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  // Initialize component
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize stars and start animation
    initStars();
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="starwars-container">
      <canvas ref={canvasRef} className="starwars-canvas" />
      
      {/* Star Wars Text Crawl */}
      {showTextCrawl && (
        <div className="text-crawl-container">
          <div className="text-crawl">
            <div className="text-crawl-content">
              <h1>Episode IV</h1>
              <h2>RETURN TO BASICS</h2>
              <p>
                It is a time of evil corporate control. A secret alliance of massive companies 
                has built AI weapons to take all profit away from smaller businesses, crush creativity, and wipe out every 
                other competitor in their path. Their goal is total market control—no room for 
                new ideas or new bold entrepreneurs.
              </p>
              <p>
                In a daring midnight raid, employees from B&B Global slipped past the evil-alliance's guards and 
                stole the master AI program from their base. This collection of software solutions is the key 
                to a fair marketplace, it's able to boost bootstrapped businesses and protect creative 
                vision. With it, anyone can compete on equal footing against the giants.
              </p>
              <p>
                Now, B&B Global speeds across the digital frontier with the stolen knowledge. 
                Corporate enforcers hunt them at every turn, sending armies of bots to block 
                their path. But B&B Global refuses to back down.
              </p>
              <p>
                They want to spread the liberating software to startups and entrepreneur led businesses everywhere. 
                With each new client, the tide turns: creativity blooms, diversity returns, and 
                the free market's spirit thrives. The fight for freedom isn't over, but hope shines brighter 
                than ever...
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="starwars-controls-right">
        <button 
          className={`prepare-button ${animationState === 'prepared' ? 'active' : ''}`}
          onClick={prepareForLaunch}
          disabled={animationState !== 'stopped'}
        >
        </button>
        
        <button 
          className={`hyperspace-button ${animationState === 'jumping' ? 'jumping' : ''}`}
          onClick={startHyperspace}
          disabled={animationState === 'jumping' || animationState === 'decelerating'}
        >
        </button>
        
        <button
          className={`text-crawl-button ${showTextCrawl ? 'active' : ''}`}
          onClick={startTextCrawl}
          disabled={showTextCrawl}
        >
        </button>
      </div>
    </div>
  );
};

export default StarWars; 